import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip
} from '@mui/material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers-pro';
import { useRef, useState } from 'react';
import { Link } from '../DetailsDrawer/detailsDrawersHelpers';
import { Icon } from '../../Atoms/Icon';
import { SrOnlyText } from '../../Atoms/Text';

export interface SalesforceLinksProps {
  options: Link[];
  sfLink: string;
}

export const SalesforceLinks = ({ options, sfLink }: SalesforceLinksProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  return (
    <Box
      className="relative"
      ref={anchorRef}>
      <ButtonGroup
        sx={{
          '& .MuiButtonGroup-grouped': {
            border: '1px solid lightgrey'
          },
          '& .MuiButtonGroup-grouped:not(:last-of-type)': {
            borderRight: '1px solid lightgrey'
          },
          '& .MuiButtonGroup-grouped:hover': {
            backgroundColor: (theme) => theme.palette.action.hover
          }
        }}
        variant="contained"
        disableElevation
        color="grey300">
        <Button
          title="Open in Salesforce"
          size="small">
          <a
            title="Open in Salesforce"
            href={sfLink}
            target="_blank"
            rel="noreferrer">
            <Icon type="external" />
            <SrOnlyText>Open in Salesforce</SrOnlyText>
          </a>
        </Button>
        <Button
          title="Salesforce links"
          color="grey300"
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select merge strategy"
          aria-haspopup="menu"
          onClick={handleToggle}>
          <ArrowDropDownIcon
            fontSize="small"
            color="buttonGrey"
          />
        </Button>
      </ButtonGroup>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-start"
        sx={{
          transform: 'translate3d(0px, 36.5px, 0px) !important'
        }}
        style={{ zIndex: 1000 }}>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'left top' : 'left bottom'
            }}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu">
                  {options.map((option, index) => {
                    return (
                      <div key={`${option.display || option.url}-${index}`}>
                        {option.display && (
                          <MenuItem className="w-full flex flex-col">
                            <a
                              className="flex space-x-1 w-full text-indigo"
                              onClick={() => setOpen(false)}
                              title={option.display}
                              href={option.url}
                              target="_blank"
                              rel="noreferrer">
                              <div className="flex items-center text-small">
                                <Tooltip
                                  arrow
                                  title="Open"
                                  placement="bottom">
                                  <div
                                    className="mr-2"
                                    data-testid="openInSalesforceButton">
                                    <Icon type="external" />
                                  </div>
                                </Tooltip>
                                {String(option.display)}
                              </div>
                            </a>
                          </MenuItem>
                        )}
                        {(index + 1) % 4 === 0 &&
                          (index + 1) % 10 !== 0 &&
                          [!!options[index].display, !!options[index - 1].display, !!options[index - 2].display].some(
                            Boolean
                          ) && (
                            <div className="my-2 w-full h-[5px] flex flex-col justify-center">
                              <hr className="bg-grey-9 w-full" />
                            </div>
                          )}
                      </div>
                    );
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};
